import { useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useTenantStore } from '@/stores/tenantStore';
import { useHasHydrated } from '@/stores/authStore';
import { applyTenantBranding } from '@/lib/tenant-branding';

import { API_URL } from '@/services/api';

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: Readonly<TenantProviderProps>) {
  const { setTenant, setLoading, setError, isResolved } = useTenantStore();
  // The tenantStore is now persisted and is the single source of truth: login /
  // checkout populate it directly, so on localhost we no longer need the old
  // authStore.tenant hint — the persisted tenant already carries the context.
  const persistedTenant = useTenantStore((s) => s.tenant);
  const hasHydrated = useHasHydrated();

  useEffect(() => {
    async function resolveTenant() {
      try {
        setLoading(true);
        const domain = window.location.hostname;

        // In localhost development there is no public domain to resolve against.
        // Use the persisted tenant (populated at login/checkout) instead of
        // hitting /public/tenant/resolve, which would 404 for `localhost`.
        if (domain === 'localhost' || domain === '127.0.0.1') {
          if (persistedTenant?.id) {
            // Already have the tenant (persisted or set at login): just mark it
            // resolved so route guards and consumers proceed.
            setTenant(persistedTenant);
            return;
          }

          // No tenant yet on localhost: skip domain resolve to avoid a false 404.
          if (hasHydrated) {
            setLoading(false);
            return;
          }
        }

        const { data } = await axios.get(`${API_URL}/public/tenant/resolve`, {
          params: { domain },
        });

        const tenant = data.data || data;

        setTenant({
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          status: tenant.status,
          subdomain: tenant.subdomain,
          appName: tenant.appName,
          logoUrl: tenant.logoUrl,
          faviconUrl: tenant.faviconUrl,
          primaryColor: tenant.primaryColor,
          accentColor: tenant.accentColor,
          layoutType: tenant.layoutType,
          clientUrl: tenant.clientUrl,
          adminUrl: tenant.adminUrl,
          features: tenant.features || {},
        });

        // Apply branding. A MESMA funcao que "Marca e aparencia" chama ao
        // salvar — duas copias divergiriam no dia em que um token novo entrasse.
        applyTenantBranding(tenant);
      } catch (err) {
        console.error('Failed to resolve tenant:', err);
        setError('Failed to resolve tenant');
      }
    }

    if (!isResolved) {
      resolveTenant();
    }
  }, [isResolved, setTenant, setLoading, setError, persistedTenant, hasHydrated]);

  return <>{children}</>;
}
